import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import Notizen from "./Notizen";
import Todo from "./ToDo";
import Reise from "./Reise";
import Kontakt from "./Kontakt";
import Projekte from "./Projekte";
import Meeting from "./Meeting";
import Angebote from "./Angebote";
import Kalender from "./Kalender";
import Posteingang from "./Posteingang";
import MailAblage from "./MailAblage";
import Ferienlager from "./Ferienlager";
import Kegeln from "./Kegeln";
import Health from "./Health";
import Passwoerter from "./Passwoerter";

const menuItems = [
  { path: "/notizen",     label: "Notizen",     icon: "📝" },
  { path: "/todo",        label: "ToDo",        icon: "✅" },
  { path: "/kalender",    label: "Kalender",    icon: "📅" },
  { path: "/posteingang", label: "Posteingang", icon: "📧" },
  { path: "/mailablage",  label: "Mails Ablage", icon: "📬" },
  { path: "/projekte", label: "Projekte", icon: "📁" },
  { path: "/meeting",  label: "Meeting",  icon: "🎙️" },
  { path: "/angebote", label: "Angebote", icon: "🏷️" },
  { path: "/reise",    label: "Reise",    icon: "✈️" },
  { path: "/kontakt",  label: "Kontakte", icon: "👤" },
  { path: "/ferienlager", label: "Ferienlager", icon: "🏕️" },
  { path: "/kegeln",     label: "Kegeln",      icon: "🎳" },
  { path: "/health",     label: "Health",      icon: "❤️" },
  { path: "/passwoerter", label: "Passwörter", icon: "🔐" },
];

// Eingeschränkte Rolle: sieht ausschließlich die Ferienlager-Anmeldungen, sonst nichts vom
// persönlichen Schreibtisch (Notizen, Mails, Projekte etc.).
const orgaMenuItems = [
  { path: "/ferienlager", label: "Anmeldungen", icon: "🏕️" },
];

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 700);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 700);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return mobile;
}

function AppRoutes({ user, role }) {
  if (role === "ferienlager-orga") {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/ferienlager" />} />
        <Route path="/ferienlager" element={<Ferienlager user={user} nurAnmeldungen />} />
        <Route path="*" element={<Navigate to="/ferienlager" />} />
      </Routes>
    );
  }
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/notizen" />} />
      <Route path="/notizen" element={<Notizen user={user} />} />
      <Route path="/todo" element={<Todo user={user} />} />
      <Route path="/kalender" element={<Kalender user={user} />} />
      <Route path="/posteingang" element={<Posteingang user={user} />} />
      <Route path="/mailablage" element={<MailAblage user={user} />} />
      <Route path="/projekte/*" element={<Projekte user={user} />} />
      <Route path="/meeting" element={<Meeting user={user} />} />
      <Route path="/angebote" element={<Angebote user={user} />} />
      <Route path="/reise" element={<Reise user={user} />} />
      <Route path="/kontakt" element={<Kontakt user={user} />} />
      <Route path="/ferienlager" element={<Ferienlager user={user} />} />
      <Route path="/kegeln" element={<Kegeln user={user} />} />
      <Route path="/health" element={<Health user={user} />} />
      <Route path="/passwoerter" element={<Passwoerter user={user} />} />
    </Routes>
  );
}

export default function Layout({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOffen, setSidebarOffen] = useState(true);
  const isMobile = useIsMobile();
  const [role, setRole] = useState(undefined); // undefined = lädt, null = keine Rolle zugewiesen

  useEffect(() => {
    const ref = doc(db, "users", user.uid);
    return onSnapshot(ref, (snap) => setRole(snap.exists() ? (snap.data().role || null) : null));
  }, [user.uid]);

  const aktiv = (path) => location.pathname.startsWith(path);

  if (role === undefined) return <div style={{ padding: "2rem" }}>Laden...</div>;

  if (role !== "admin" && role !== "ferienlager-orga") {
    return (
      <div style={styles.container}>
        <h2>🔒 Kein Zugriff</h2>
        <p style={{ color: "#666", maxWidth: "360px" }}>
          Dieses Konto ({user.email}) ist angemeldet, hat aber noch keine Rolle zugewiesen bekommen.
          Bitte beim Admin melden.
        </p>
        <button style={styles.abmeldenTextBtn} onClick={() => signOut(auth)}>Abmelden</button>
      </div>
    );
  }

  const items = role === "admin" ? menuItems : orgaMenuItems;

  if (isMobile) {
    const aktuellerItem = items.find((m) => aktiv(m.path));
    return (
      <div style={styles.mobileWrapper}>
        <div style={styles.topBar}>
          <span style={styles.topBarTitel}>
            {aktuellerItem ? `${aktuellerItem.icon} ${aktuellerItem.label}` : "Schreibtisch"}
          </span>
          <button style={styles.abmeldenMobile} onClick={() => signOut(auth)} title="Abmelden">🚪</button>
        </div>
        <div style={styles.mobileContent}>
          <AppRoutes user={user} role={role} />
        </div>
        <nav style={styles.bottomNav}>
          {items.map((item) => (
            <button
              key={item.path}
              style={{ ...styles.bottomNavBtn, color: aktiv(item.path) ? "#1a73e8" : "#666", borderTop: aktiv(item.path) ? "2px solid #1a73e8" : "2px solid transparent" }}
              onClick={() => navigate(item.path)}
            >
              <span style={styles.bottomNavIcon}>{item.icon}</span>
              <span style={styles.bottomNavLabel}>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <div style={{ ...styles.sidebar, width: sidebarOffen ? "200px" : "56px" }}>
        <div style={styles.sidebarHeader} onClick={() => setSidebarOffen(!sidebarOffen)}>
          {sidebarOffen ? "◀ Schreibtisch" : "▶"}
        </div>
        <nav>
          {items.map((item) => (
            <div
              key={item.path}
              style={{ ...styles.menuItem, backgroundColor: aktiv(item.path) ? "#2d4a7a" : "transparent" }}
              onClick={() => navigate(item.path)}
            >
              <span style={styles.icon}>{item.icon}</span>
              {sidebarOffen && <span>{item.label}</span>}
            </div>
          ))}
        </nav>
        <div style={styles.sidebarFooter}>
          {sidebarOffen && <span style={styles.email}>{user.email}</span>}
          <button style={styles.abmelden} onClick={() => signOut(auth)} title="Abmelden">🚪</button>
        </div>
      </div>
      <div style={styles.content}>
        <AppRoutes user={user} role={role} />
      </div>
    </div>
  );
}

const styles = {
  container: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", fontFamily: "sans-serif", textAlign: "center", padding: "2rem", gap: "0.6rem" },
  abmeldenTextBtn: { marginTop: "0.6rem", padding: "0.6rem 1.4rem", background: "#1a73e8", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "0.9rem" },

  // Desktop
  wrapper: { display: "flex", height: "100vh", fontFamily: "sans-serif" },
  sidebar: { backgroundColor: "#1a3558", color: "white", display: "flex", flexDirection: "column", transition: "width 0.2s", overflow: "hidden", flexShrink: 0 },
  sidebarHeader: { padding: "1rem", fontWeight: "bold", cursor: "pointer", borderBottom: "1px solid #2d4a7a", whiteSpace: "nowrap" },
  menuItem: { display: "flex", alignItems: "center", gap: "0.7rem", padding: "0.8rem 1rem", cursor: "pointer", whiteSpace: "nowrap", borderRadius: "4px", margin: "2px 4px" },
  icon: { fontSize: "1.1rem", flexShrink: 0 },
  sidebarFooter: { marginTop: "auto", padding: "0.8rem", borderTop: "1px solid #2d4a7a", display: "flex", alignItems: "center", gap: "0.5rem" },
  email: { fontSize: "0.75rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  abmelden: { background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem", flexShrink: 0 },
  content: { flex: 1, overflow: "auto", backgroundColor: "#f0f2f5" },

  // Mobile
  mobileWrapper: { display: "flex", flexDirection: "column", height: "100vh", fontFamily: "sans-serif" },
  topBar: { backgroundColor: "#1a3558", color: "white", padding: "0.8rem 1rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, boxShadow: "0 2px 6px rgba(0,0,0,0.2)" },
  topBarTitel: { fontWeight: "bold", fontSize: "1rem" },
  abmeldenMobile: { background: "none", border: "none", cursor: "pointer", fontSize: "1.3rem", color: "white" },
  mobileContent: { flex: 1, overflow: "auto", backgroundColor: "#f0f2f5" },
  bottomNav: { display: "flex", backgroundColor: "white", borderTop: "1px solid #e0e0e0", flexShrink: 0, boxShadow: "0 -2px 8px rgba(0,0,0,0.08)" },
  bottomNavBtn: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "0.5rem 0.1rem", background: "none", border: "none", cursor: "pointer", gap: "1px" },
  bottomNavIcon: { fontSize: "1.2rem" },
  bottomNavLabel: { fontSize: "0.55rem", fontWeight: "500" },
};
